import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import EmbeddedClinicList from "../../components/EmbeddedClinicList";
import {
  TwoColRow,
  ColText,
  ColImage,
  ColImg,
  SectionBottomImage,
} from "../../components/ContentLayouts";

// ── Image imports ───────────────────────────────────────────────────────────────
import imgDermatologyNearMe from "../../assets/images/content/pet-dermatology/dermatology-nearme.jpg";
import imgDermatologyMatters from "../../assets/images/content/pet-dermatology/dermatology-matters.jpg";

const faqs = [
  {
    question: "What skin conditions can I find treatment for through PetQura?",
    answer:
      "You can find vets who treat a wide range of pet skin issues, including allergies, bacterial and fungal infections, hot spots, mange, and chronic itching. A pet dermatologist near me can help identify the exact cause behind your pet's symptoms.",
  },
  {
    question: "Why is my pet constantly scratching or licking?",
    answer:
      "Constant scratching or licking is often a sign of allergies, dry skin, parasites, or an underlying infection. It's best not to ignore this behaviour, since it usually points to something that needs proper care rather than simple discomfort.",
  },
  {
    question: "How are pet skin allergies diagnosed?",
    answer:
      "A vet dermatologist typically starts with a physical exam, followed by tests such as skin scrapings, blood work, or a diet trial, depending on the suspected cause of the reaction.",
  },
  {
    question: "When should I visit a veterinary dermatologist?",
    answer:
      "If your pet has ongoing scratching, hair loss, rashes, or infections that keep returning, it's a good time to search for a vet dermatologist near me. Early visits usually mean simpler, quicker treatment.",
  },
  {
    question: "Can chronic skin conditions be treated?",
    answer:
      "Yes, many chronic skin conditions can be managed effectively with the right treatment plan, even if they can't always be fully cured. Ongoing care and regular check-ups usually help keep symptoms under control.",
  },
  {
    question: "How much does a dermatology consultation cost?",
    answer:
      "Costs vary depending on the clinic and the treatment your pet needs. When you book through PetQura, you can view relevant clinic details before confirming your appointment.",
  },
  {
    question:
      "How do I book an appointment with a dog dermatologist near me through PetQura?",
    answer:
      "Simply enter your location in Bangalore, select dermatology as the service, and browse clinics near you. Once you find one that suits you, book directly through the platform.",
  },
  {
    question: "Is PetQura available across all of Bangalore?",
    answer:
      "Yes, PetQura helps pet parents across Bangalore find a pet dermatologist near you, along with other veterinary services, based on your specific location.",
  },
];

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqRow>
      <FaqQuestion onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <FaqIcon>{open ? "−" : "+"}</FaqIcon>
      </FaqQuestion>
      {open && <FaqAnswer>{answer}</FaqAnswer>}
    </FaqRow>
  );
};

const PetDermatology = () => {
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Pet Dermatologist Near You in Bangalore | PetQura</title>
        <meta
          name="description"
          content="Find a trusted pet dermatologist near you in Bangalore. From skin allergies to coat conditions, connect with specialist vets and book a dermatology consultation online."
        />
      </Helmet>

      <PageWrapper>
        <HeroSection>
          <h1>Pet Dermatologist Near You in Bangalore by PetQura</h1>
          <EmbeddedClinicList serviceId={null} previewMode />
          <TwoColRow>
            <ColText>
              <p>
                If your dog won't stop scratching, or your cat has developed
                bald patches and irritated skin, you're probably already
                searching for a <em>vet dermatologist near me</em>. We
                understand how unsettling it is to watch your pet
                uncomfortable and not know where to take them. At PetQura, we
                make that search simple. We help pet parents across
                Bangalore find the right dermatologist for their pet, based
                on their location and the exact service needed.
              </p>
              <p>
                Skin issues like allergies, infections, and constant itching
                are among the most common reasons pets visit the vet, and
                they rarely resolve on their own. Instead of calling clinic
                after clinic, you can use PetQura to filter by service,
                compare nearby clinics, and book an appointment in minutes.
              </p>
            </ColText>
            <ColImage>
              <ColImg
                src={imgDermatologyNearMe}
                alt="Finding a vet dermatologist near you in Bangalore"
              />
            </ColImage>
          </TwoColRow>
        </HeroSection>

        {/* ── Signs ── */}
        <ContentSection>
          <SectionHeading>
            Signs Your Pet Might Need a Vet Dermatologist
          </SectionHeading>
          <p>
            Pets can't tell us when their skin is bothering them, so it helps
            to know what to look for. Catching problems early usually means
            simpler, faster treatment. Here are common signs that it's time to
            search for a pet dermatologist near you:
          </p>
          <BulletList>
            <li>Excessive scratching, licking, or chewing at the skin</li>
            <li>Redness, rashes, or visible irritation</li>
            <li>Hair loss or bald patches in one area</li>
            <li>Recurring ear infections or a persistent odour</li>
            <li>Scabs, sores, or thickened skin</li>
            <li>Sudden changes in coat texture or heavy shedding</li>
          </BulletList>
          <p>
            If your pet shows even one or two of these signs, it's worth getting
            them checked rather than waiting for things to improve.
          </p>
        </ContentSection>

        {/* ── Why It Matters ── */}
        <ContentSection>
          <SectionHeading>
            Why a Pet Dermatologist Near You Matters for Your Pet
          </SectionHeading>
          <p>
            It's tempting to assume mild scratching or a small rash will clear
            up on its own. But untreated skin issues rarely stay minor. What
            starts as light irritation can quickly turn into open wounds, hair
            loss, or infections that take longer, and cost more, to treat.
          </p>
          <p>
            Seeing a vet dermatologist near me isn't only for emergencies.
            Regular check-ins help catch allergies and infections early, before
            they affect your pet's comfort and daily routine. The longer skin
            problems go unaddressed, the harder they usually are to manage.
          </p>
          <p>
            If you've noticed any of the signs above, don't wait for them to
            worsen. Book an appointment with a pet dermatologist near you
            through PetQura today, and get your pet the care they need without
            the hassle of finding a clinic on your own.
          </p>
          <SectionBottomImage
            src={imgDermatologyMatters}
            alt="Why seeing a pet dermatologist near you matters"
          />
        </ContentSection>

        {/* ── How PetQura Helps ── */}
        <ContentSection>
          <SectionHeading>
            How PetQura Helps To Find a Pet Dermatologist Near Me
          </SectionHeading>
          <p>
            Finding the right clinic shouldn't take up your evening. With
            PetQura, you simply enter your location within Bangalore, select
            dermatology as the service you need, and we show you nearby clinics
            that offer it.
          </p>
          <p>
            Whether you're searching for a dog dermatologist or a specialist for
            your cat, you'll see clear details for each clinic, helping you
            decide what works best for you and your pet. Once you've chosen a
            clinic, booking takes just a few taps, with no long phone calls and
            no waiting for a callback.
          </p>
          <p>
            This means less time spent searching and more time getting your pet
            seen by a vet dermatologist near me, without added stress for either
            of you.
          </p>
        </ContentSection>

        {/* ── What to Expect ── */}
        <ContentSection>
          <SectionHeading>
            What to Expect During a Pet Skin Consultation
          </SectionHeading>
          <p>
            A dermatologist typically begins with a physical examination of your
            pet's skin, coat, and ears to look for patterns behind the
            irritation. Depending on what they find, they may suggest further
            tests such as skin scrapings, allergy testing, or a short diet
            trial. Treatment is usually tailored to the underlying cause and may
            include:
          </p>
          <BulletList>
            <li>Medicated shampoos or topical treatments</li>
            <li>Oral medication for infections or allergies</li>
            <li>Dietary changes for food-related reactions</li>
            <li>Ongoing monitoring for chronic conditions</li>
          </BulletList>
          <p>
            For pets with recurring issues, follow-up visits are often part of
            the plan to keep symptoms under control over time. Whatever the
            diagnosis, the goal stays the same: helping your pet feel
            comfortable again, without unnecessary guesswork.
          </p>
        </ContentSection>

        {/* ── FAQ ── */}
        <ContentSection>
          <SectionHeading>Frequently Asked Questions (FAQs)</SectionHeading>
          <FaqList>
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </FaqList>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default PetDermatology;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5% 5rem;

  @media (max-width: 768px) {
    padding: 2rem 5% 4rem;
  }
`;

const HeroSection = styled.section`
  margin-bottom: 3rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 1.5rem;
    font-family: Montserrat, sans-serif;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const ContentSection = styled.section`
  margin-bottom: 2.5rem;

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1rem;
  font-family: Montserrat, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BulletList = styled.ul`
  padding-left: 1.4rem;
  margin-bottom: 1rem;

  li {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 0.6rem;
  }
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const FaqRow = styled.div`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: none;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f7f9ff;
  }
`;

const FaqIcon = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: #00419d;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const FaqAnswer = styled.div`
  padding: 0 1.25rem 1rem;
  font-size: 15px;
  line-height: 1.75;
  color: #555555;
  font-family: Montserrat, sans-serif;
  background: #fafafa;
`;
